/**
 * GPN Admin Portal - Centralized Static Translations Dictionary
 * Single source of truth for all static labels, navigation, buttons, table headers,
 * status badges, and settings across the entire Admin Portal.
 */

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸', shortCode: 'EN' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు', flag: '🇮🇳', shortCode: 'TE' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳', shortCode: 'HI' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்', flag: '🇮🇳', shortCode: 'TA' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', shortCode: 'ES' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', shortCode: 'FR' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', shortCode: 'DE' },
];

export const staticTranslations = {
  // ── ENGLISH (Base) ────────────────────────────────────────────────────────
  en: {
    // Top Navigation & Branding
    brand_title: 'Glocal Prayer Network',
    brand_tagline: 'Uniting the Global Body of Christ • Prayer, Devotion & Generosity',
    admin_badge: 'Admin',
    super_admin_badge: 'Super Admin',
    compliance_tracker: 'Agreement Compliance',
    agreement_pending: 'Agreement Pending',
    select_language: 'Select Language',
    exit_org_view: 'Exit',
    switch_org: 'Switch Org',
    visit_website: 'Visit Glocal Prayer Network Website',

    // Sidebar Navigation Items
    nav_dashboard: 'Dashboard',
    nav_users: 'Users',
    nav_prayers: 'Prayers',
    nav_prayer_closet: 'Prayer Closet',
    nav_share_and_accept: 'Share & Accept',
    nav_ride_connect: 'Ride Connect',
    nav_songs: 'Songs',
    nav_events: 'Events',
    nav_daily_devotions: 'Daily Devotion',
    nav_testimonies: 'Testimonies',
    nav_feedback: 'Feedbacks',
    nav_groups: 'Groups',
    nav_organizations: 'Organizations',
    nav_settings: 'Settings',
    nav_admin_agreement: 'Admin Agreement',
    nav_guide: 'Guide & Help',

    // Common Buttons & Actions
    btn_save: 'Save',
    btn_cancel: 'Cancel',
    btn_edit: 'Edit',
    btn_delete: 'Delete',
    btn_create: 'Create',
    btn_add: 'Add New',
    btn_search: 'Search',
    btn_filter: 'Filter',
    btn_export: 'Export',
    btn_import: 'Import',
    btn_submit: 'Submit',
    btn_back: 'Back',
    btn_confirm: 'Confirm',
    btn_refresh: 'Refresh',
    btn_close: 'Close',
    btn_view: 'View',
    btn_actions: 'Actions',
    btn_reset: 'Reset to Default',
    btn_apply: 'Apply Changes',
    btn_switch: 'Switch',
    btn_logout: 'Logout',
    btn_profile: 'My Profile',
    btn_change_password: 'Change Password',
    btn_add_language: 'Add Language',
    btn_save_default_lang: 'Save Default Language',

    // Statuses & Indicators
    status_active: 'Active',
    status_inactive: 'Inactive',
    status_pending: 'Pending',
    status_completed: 'Completed',
    status_booked: 'Booked',
    status_available: 'Available',
    status_success: 'Success',
    status_error: 'Error',
    status_warning: 'Warning',
    status_loading: 'Loading...',
    status_no_data: 'No data found',
    priority_critical: 'Critical',
    priority_high: 'High',
    priority_medium: 'Medium',
    priority_low: 'Low',

    // Table Headers
    th_hash: '#',
    th_language: 'Language',
    th_short_code: 'Short Code',
    th_actions: 'Actions',
    th_name: 'Name',
    th_email: 'Email',
    th_role: 'Role',
    th_organization: 'Organization',
    th_created_at: 'Created At',
    th_status: 'Status',
    th_title: 'Title',
    th_category: 'Category',

    // Settings Tabs & Sections
    settings_title: 'Settings & Configurations',
    settings_subtitle: 'Configure organization defaults, languages, roles & permissions, module approvals, and mobile app layouts.',
    tab_languages: 'Languages',
    tab_roles_permissions: 'Roles & Permissions',
    tab_approval_settings: 'Approval Settings',
    tab_feature_settings: 'Feature Settings',
    tab_app_home_settings: 'APP Home Page Settings',
    tab_app_bottom_bar: 'App Bottom Bar Settings',
    tab_sunday_worship: 'Sunday Worship & Bible Study',
    tab_faith_statement: 'Faith Statement Settings',
    tab_my_daily_devotion: 'My Daily Devotion',
    org_default_language: 'Organization Default Language',
    org_default_lang_desc: 'Choose the primary language loaded automatically for mobile app users belonging to this organization.',
    how_it_works: 'How it works',
    how_it_works_lang_desc: 'Mobile app users will automatically receive this default language upon sign-in. Users can still manually switch their preferred language anytime in their mobile app profile.',
    select_default_language: 'Select Default Language',
    current_active_default: 'Current Active Default',
    saving_default_lang: 'Saving Default Language...',
    select_organization_to_configure: 'Select Organization to Configure',
    system_supported_languages: 'System Supported Languages',
    search_language_placeholder: 'Search language or code...',
    select_default_lang_placeholder: 'Select Default Language...',
    th_languages: 'Languages',
    edit_language: 'Edit Language',
    add_new_language: 'Add New Language',
    field_language_name: 'Language Name',
    field_short_code: 'Short Code (e.g. en, te, hi)',

    // Language Names
    lang_name_english: 'English',
    lang_name_telugu: 'Telugu',
    lang_name_hindi: 'Hindi',
    lang_name_tamil: 'Tamil',
    lang_name_spanish: 'Spanish',
    lang_name_french: 'French',
    lang_name_german: 'German',
    english: 'English',
    telugu: 'Telugu',
    hindi: 'Hindi',
    tamil: 'Tamil',
    spanish: 'Spanish',

    // Theme & User Actions
    theme_dark_mode: 'Switch to Dark Mode',
    theme_light_mode: 'Switch to Light Mode',
  },

  // ── TELUGU (తెలుగు) ──────────────────────────────────────────────────────────
  te: {
    // Top Navigation & Branding
    brand_title: 'గ్లోకల్ ప్రార్థన నెట్‌వర్క్',
    brand_tagline: 'క్రీస్తు శరీరంలో ప్రపంచవ్యాప్త ఐక్యత • ప్రార్థన, భక్తి మరియు దాతృత్వం',
    admin_badge: 'అడ్మిన్',
    super_admin_badge: 'సూపర్ అడ్మిన్',
    compliance_tracker: 'ఒప్పంద సమీక్ష (Compliance)',
    agreement_pending: 'ఒప్పందం పెండింగ్‌లో ఉంది',
    select_language: 'భాషను ఎంచుకోండి',
    exit_org_view: 'నిష్క్రమించు',
    switch_org: 'సంస్థను మార్చు',
    visit_website: 'గ్లోకల్ ప్రార్థన నెట్‌వర్క్ వెబ్‌సైట్ సందర్శించండి',

    // Sidebar Navigation Items
    nav_dashboard: 'డాష్‌బోర్డ్',
    nav_users: 'వినియోగదారులు',
    nav_prayers: 'ప్రార్థనలు',
    nav_prayer_closet: 'ప్రార్థన గది (Prayer Closet)',
    nav_share_and_accept: 'భాగస్వామ్యం & స్వీకరణ',
    nav_ride_connect: 'రైడ్ కనెక్ట్',
    nav_songs: 'పాటలు',
    nav_events: 'కార్యక్రమాలు (ఈవెంట్స్)',
    nav_daily_devotions: 'రోజువారీ భక్తి వాక్యం',
    nav_testimonies: 'సాక్ష్యాలు',
    nav_feedback: 'అభిప్రాయాలు',
    nav_groups: 'సమూహాలు (గ్రూపులు)',
    nav_organizations: 'సంస్థలు',
    nav_settings: 'సెట్టింగ్‌లు',
    nav_admin_agreement: 'అడ్మిన్ ఒప్పందం',
    nav_guide: 'మార్గదర్శి & సహాయం',

    // Common Buttons & Actions
    btn_save: 'భద్రపరచు (Save)',
    btn_cancel: 'రద్దు చేయి (Cancel)',
    btn_edit: 'సవరించు (Edit)',
    btn_delete: 'తొలగించు (Delete)',
    btn_create: 'సృష్టించు',
    btn_add: 'క్రొత్తది జోడించు',
    btn_search: 'వెతుకు (Search)',
    btn_filter: 'ఫిల్టర్',
    btn_export: 'ఎగుమతి చేయి',
    btn_import: 'దిగుమతి చేయి',
    btn_submit: 'సమర్పించు',
    btn_back: 'వెనుకకు',
    btn_confirm: 'ధృవీకరించు',
    btn_refresh: 'తాజాకరించు',
    btn_close: 'మూసివేయి',
    btn_view: 'చూడండి',
    btn_actions: 'చర్యలు',
    btn_reset: 'డిఫాల్ట్‌కు మార్చు',
    btn_apply: 'మార్పులను వర్తింపజేయి',
    btn_switch: 'మార్చండి',
    btn_logout: 'లాగౌట్',
    btn_profile: 'నా ప్రొఫైల్',
    btn_change_password: 'పాస్‌వర్డ్ మార్చండి',
    btn_add_language: 'భాషను జోడించు',
    btn_save_default_lang: 'డిఫాల్ట్ భాషను భద్రపరచు',

    // Statuses & Indicators
    status_active: 'క్రియాశీలం (Active)',
    status_inactive: 'నిష్క్రియం',
    status_pending: 'పెండింగ్‌లో ఉంది',
    status_completed: 'పూర్తయింది',
    status_booked: 'బుక్ చేయబడింది',
    status_available: 'లభ్యమవుతుంది',
    status_success: 'విజయం',
    status_error: 'లోపం',
    status_warning: 'హెచ్చరిక',
    status_loading: 'లోడ్ అవుతోంది...',
    status_no_data: 'డేటా కనుగొనబడలేదు',
    priority_critical: 'అత్యవసరం (Critical)',
    priority_high: 'ఎక్కువ (High)',
    priority_medium: 'మధ్యస్థం (Medium)',
    priority_low: 'సాధారణం (Low)',

    // Table Headers
    th_hash: '#',
    th_language: 'భాష',
    th_short_code: 'సంక్షిప్త కోడ్',
    th_actions: 'చర్యలు',
    th_name: 'పేరు',
    th_email: 'ఈమెయిల్',
    th_role: 'పాత్ర (Role)',
    th_organization: 'సంస్థ',
    th_created_at: 'సృష్టించిన తేదీ',
    th_status: 'స్థితి',
    th_title: 'శీర్షిక',
    th_category: 'వర్గం',

    // Settings Tabs & Sections
    settings_title: 'సెట్టింగ్‌లు & కాన్ఫిగరేషన్‌లు',
    settings_subtitle: 'సంస్థ డిఫాల్ట్‌లు, భాషలు, పాత్రలు & అనుమతులు, మాడ్యూల్ ఆమోదాలు మరియు మొబైల్ లేఅవుట్‌లను కాన్ఫిగర్ చేయండి.',
    tab_languages: 'భాషలు (Languages)',
    tab_roles_permissions: 'పాత్రలు & అనుమతులు',
    tab_approval_settings: 'ఆమోద సెట్టింగ్‌లు',
    tab_feature_settings: 'ఫీచర్ సెట్టింగ్‌లు',
    tab_app_home_settings: 'యాప్ హోమ్ పేజీ సెట్టింగ్‌లు',
    tab_app_bottom_bar: 'యాప్ బాటమ్ బార్ సెట్టింగ్‌లు',
    tab_sunday_worship: 'ఆదివార ఆరాధన & బైబిల్ స్టడీ',
    tab_faith_statement: 'విశ్వాస ప్రకటన సెట్టింగ్‌లు',
    tab_my_daily_devotion: 'నా రోజువారీ భక్తి వాక్యం',
    org_default_language: 'సంస్థ డిఫాల్ట్ భాష',
    org_default_lang_desc: 'ఈ సంస్థకు చెందిన మొబైల్ వినియోగదారుల కోసం స్వయంచాలకంగా లోడ్ అయ్యే ప్రాథమిక భాషను ఎంచుకోండి.',
    how_it_works: 'ఇది ఎలా పనిచేస్తుంది',
    how_it_works_lang_desc: 'మొబైల్ యాప్ వినియోగదారులు సైన్-ఇన్ చేసినప్పుడు స్వయంచాలకంగా ఈ డిఫాల్ట్ భాషను అందుకుంటారు. వినియోగదారులు తమ మొబైల్ యాప్ ప్రొఫైల్‌లో ఎప్పుడైనా తమ ప్రాధాన్యత భాషను మార్చుకోవచ్చు.',
    select_default_language: 'డిఫాల్ట్ భాషను ఎంచుకోండి',
    current_active_default: 'ప్రస్తుత డిఫాల్ట్ భాష',
    saving_default_lang: 'డిఫాల్ట్ భాషను భద్రపరుస్తోంది...',
    select_organization_to_configure: 'కాన్ఫిగర్ చేయడానికి సంస్థను ఎంచుకోండి',
    system_supported_languages: 'సిస్టమ్ మద్దతు ఇచ్చే భాషలు',
    search_language_placeholder: 'భాష లేదా కోడ్ ద్వారా వెతకండి...',
    select_default_lang_placeholder: 'డిఫాల్ట్ భాషను ఎంచుకోండి...',
    th_languages: 'భాషలు',
    edit_language: 'భాషను సవరించండి',
    add_new_language: 'కొత్త భాషను జోడించండి',
    field_language_name: 'భాష పేరు',
    field_short_code: 'సంక్షిప్త కోడ్ (ఉదా. en, te, hi)',

    // Language Names
    lang_name_english: 'ఇంగ్లీష్',
    lang_name_telugu: 'తెలుగు',
    lang_name_hindi: 'హిందీ',
    lang_name_tamil: 'తమిళం',
    lang_name_spanish: 'స్పానిష్',
    lang_name_french: 'ఫ్రెంచ్',
    lang_name_german: 'జర్మన్',
    english: 'ఇంగ్లీష్',
    telugu: 'తెలుగు',
    hindi: 'హిందీ',
    tamil: 'తమిళం',
    spanish: 'స్పానిష్',

    // Theme & User Actions
    theme_dark_mode: 'డార్క్ మోడ్‌కు మార్చు',
    theme_light_mode: 'లైట్ మోడ్‌కు మార్చు',
  },

  // ── HINDI (हिन्दी) ──────────────────────────────────────────────────────────
  hi: {
    // Top Navigation & Branding
    brand_title: 'ग्लोकल प्रार्थना नेटवर्क',
    brand_tagline: 'मसीह की वैश्विक देह में एकता • प्रार्थना, भक्ति और उदारता',
    admin_badge: 'व्यवस्थापक',
    super_admin_badge: 'मुख्य व्यवस्थापक',
    compliance_tracker: 'अनुबंध अनुपालन (Compliance)',
    agreement_pending: 'अनुबंध लंबित है',
    select_language: 'भाषा चुनें',
    exit_org_view: 'बाहर निकलें',
    switch_org: 'संस्था बदलें',
    visit_website: 'ग्लोकल प्रार्थना नेटवर्क वेबसाइट पर जाएँ',

    // Sidebar Navigation Items
    nav_dashboard: 'डैशबोर्ड',
    nav_users: 'उपयोगकर्ता',
    nav_prayers: 'प्रार्थनाएँ',
    nav_prayer_closet: 'प्रार्थना कोठरी (Prayer Closet)',
    nav_share_and_accept: 'साझा करें और स्वीकारें',
    nav_ride_connect: 'सवारी संपर्क (Ride Connect)',
    nav_songs: 'गीत एवं भजन',
    nav_events: 'कार्यक्रम (इवेंट्स)',
    nav_daily_devotions: 'दैनिक भक्ति वचन',
    nav_testimonies: 'गवाहियाँ',
    nav_feedback: 'प्रतिक्रियाएँ',
    nav_groups: 'समूह (ग्रुप्स)',
    nav_organizations: 'संस्थाएँ',
    nav_settings: 'सेटिंग्स',
    nav_admin_agreement: 'व्यवस्थापक अनुबंध',
    nav_guide: 'मार्गदर्शिका एवं सहायता',

    // Common Buttons & Actions
    btn_save: 'सहेजें (Save)',
    btn_cancel: 'रद्द करें (Cancel)',
    btn_edit: 'संपादित करें (Edit)',
    btn_delete: 'हटाएँ (Delete)',
    btn_create: 'बनाएँ',
    btn_add: 'नया जोड़ें',
    btn_search: 'खोजें (Search)',
    btn_filter: 'फ़िल्टर',
    btn_export: 'निर्यात करें',
    btn_import: 'आयात करें',
    btn_submit: 'प्रस्तुत करें',
    btn_back: 'वापस',
    btn_confirm: 'पुष्टि करें',
    btn_refresh: 'ताज़ा करें',
    btn_close: 'बंद करें',
    btn_view: 'देखें',
    btn_actions: 'कार्रवाई',
    btn_reset: 'डिफ़ॉल्ट पर रीसेट करें',
    btn_apply: 'लागू करें',
    btn_switch: 'बदलें',
    btn_logout: 'लॉग आउट',
    btn_profile: 'मेरी प्रोफ़ाइल',
    btn_change_password: 'पासवर्ड बदलें',
    btn_add_language: 'भाषा जोड़ें',
    btn_save_default_lang: 'डिफ़ॉल्ट भाषा सहेजें',

    // Statuses & Indicators
    status_active: 'सक्रिय (Active)',
    status_inactive: 'निष्क्रिय',
    status_pending: 'लंबित',
    status_completed: 'पूर्ण',
    status_booked: 'आरक्षित (Booked)',
    status_available: 'उपलब्ध',
    status_success: 'सफलता',
    status_error: 'त्रुटि',
    status_warning: 'चेतावनी',
    status_loading: 'लोड हो रहा है...',
    status_no_data: 'कोई डेटा नहीं मिला',
    priority_critical: 'अति महत्वपूर्ण (Critical)',
    priority_high: 'उच्च (High)',
    priority_medium: 'मध्यम (Medium)',
    priority_low: 'सामान्य (Low)',

    // Table Headers
    th_hash: '#',
    th_language: 'भाषा',
    th_short_code: 'लघु कोड',
    th_actions: 'कार्रवाई',
    th_name: 'नाम',
    th_email: 'ईमेल',
    th_role: 'भूमिका (Role)',
    th_organization: 'संस्था',
    th_created_at: 'रचना तिथि',
    th_status: 'स्थिति',
    th_title: 'शीर्षक',
    th_category: 'श्रेणी',

    // Settings Tabs & Sections
    settings_title: 'सेटिंग्स एवं कॉन्फ़िगरेशन',
    settings_subtitle: 'संगठन डिफ़ॉल्ट, भाषाएं, भूमिकाएं एवं अनुमतियां, मॉड्यूल अनुमोदन और मोबाइल ऐप लेआउट कॉन्फ़िगर करें।',
    tab_languages: 'भाषाएं (Languages)',
    tab_roles_permissions: 'भूमिकाएं एवं अनुमतियां',
    tab_approval_settings: 'अनुमोदन सेटिंग्स',
    tab_feature_settings: 'सुविधा सेटिंग्स',
    tab_app_home_settings: 'ऐप होम पेज सेटिंग्स',
    tab_app_bottom_bar: 'ऐप बॉटम बार सेटिंग्स',
    tab_sunday_worship: 'रविवार आराधना एवं बाइबिल अध्ययन',
    tab_faith_statement: 'विश्वास वक्तव्य सेटिंग्स',
    tab_my_daily_devotion: 'मेरी दैनिक भक्ति',
    org_default_language: 'संगठन की डिफ़ॉल्ट भाषा',
    org_default_lang_desc: 'इस संगठन के मोबाइल उपयोगकर्ताओं के लिए स्वतः लोड होने वाली प्राथमिक भाषा चुनें।',
    how_it_works: 'यह कैसे काम करता है',
    how_it_works_lang_desc: 'मोबाइल ऐप उपयोगकर्ताओं को साइन-इन करने पर स्वचालित रूप से यह डिफ़ॉल्ट भाषा प्राप्त होगी। उपयोगकर्ता कभी भी अपने मोबाइल ऐप प्रोफ़ाइल में अपनी पसंदीदा भाषा बदल सकते हैं।',
    select_default_language: 'डिफ़ॉल्ट भाषा चुनें',
    current_active_default: 'वर्तमान सक्रिय डिफ़ॉल्ट',
    saving_default_lang: 'डिफ़ॉल्ट भाषा सहेजी जा रही है...',
    select_organization_to_configure: 'कॉन्फ़िगर करने के लिए संगठन चुनें',
    system_supported_languages: 'सिस्टम समर्थित भाषाएं',
    search_language_placeholder: 'भाषा या कोड खोजें...',
    select_default_lang_placeholder: 'डिफ़ॉल्ट भाषा चुनें...',
    th_languages: 'भाषाएँ',
    edit_language: 'भाषा संपादित करें',
    add_new_language: 'नई भाषा जोड़ें',
    field_language_name: 'भाषा का नाम',
    field_short_code: 'संक्षिप्त कोड (उदा. en, te, hi)',

    // Language Names
    lang_name_english: 'अंग्रेज़ी',
    lang_name_telugu: 'तेलुगु',
    lang_name_hindi: 'हिन्दी',
    lang_name_tamil: 'तमिल',
    lang_name_spanish: 'स्पैनिश',
    lang_name_french: 'फ़्रेंच',
    lang_name_german: 'जर्मन',
    english: 'अंग्रेज़ी',
    telugu: 'तेलुगु',
    hindi: 'हिन्दी',
    tamil: 'तमिल',
    spanish: 'स्पैनिश',

    // Theme & User Actions
    theme_dark_mode: 'डार्क मोड पर बदलें',
    theme_light_mode: 'लाइट मोड पर बदलें',
  },

  // ── TAMIL (தமிழ்) ──────────────────────────────────────────────────────────
  ta: {
    // Top Navigation & Branding
    brand_title: 'குளோபல் பிரேயர் நெட்வொர்க்',
    brand_tagline: 'கிறிஸ்துவின் உடலில் உலகளாவிய ஒற்றுமை • ஜெபம், பக்தி மற்றும் தயாள குணம்',
    admin_badge: 'நிர்வாகி',
    super_admin_badge: 'தலைமை நிர்வாகி',
    compliance_tracker: 'ஒப்பந்த இணக்கம்',
    agreement_pending: 'ஒப்பந்தம் நிலுவையில் உள்ளது',
    select_language: 'மொழியைத் தேர்ந்தெடுக்கவும்',
    exit_org_view: 'வெளியேறு',
    switch_org: 'அமைப்பை மாற்று',
    visit_website: 'இணையதளத்தைப் பார்வையிடவும்',

    // Sidebar Navigation Items
    nav_dashboard: 'டாஷ்போர்டு',
    nav_users: 'பயனர்கள்',
    nav_prayers: 'ஜெபங்கள்',
    nav_prayer_closet: 'ஜெப அறை (Prayer Closet)',
    nav_share_and_accept: 'பகிர்வு & ஏற்பு',
    nav_ride_connect: 'பயண இணைப்பு',
    nav_songs: 'பாடல்கள்',
    nav_events: 'நிகழ்வுகள்',
    nav_daily_devotions: 'தினசரி தியானம்',
    nav_testimonies: 'சாட்சிகள்',
    nav_feedback: 'கருத்துகள்',
    nav_groups: 'குழுக்கள்',
    nav_organizations: 'அமைப்புகள்',
    nav_settings: 'அமைப்புகள் (Settings)',
    nav_admin_agreement: 'நிர்வாக ஒப்பந்தம்',
    nav_guide: 'வழிகாட்டி & உதவி',

    // Common Buttons & Actions
    btn_save: 'சேமிக்க (Save)',
    btn_cancel: 'ரத்து செய் (Cancel)',
    btn_edit: 'திருத்து (Edit)',
    btn_delete: 'நீக்கு (Delete)',
    btn_create: 'உருவாக்கு',
    btn_add: 'புதியதைச் சேர்',
    btn_search: 'தேடு (Search)',
    btn_filter: 'வடிகட்டு',
    btn_export: 'ஏற்றுமதி செய்',
    btn_import: 'இறக்குமதி செய்',
    btn_submit: 'சமர்ப்பி',
    btn_back: 'பின்செல்',
    btn_confirm: 'உறுதிப்படுத்து',
    btn_refresh: 'புதுப்பி',
    btn_close: 'மூடு',
    btn_view: 'பார்',
    btn_actions: 'செயல்கள்',
    btn_reset: 'இயல்புநிலைக்கு மீட்டமை',
    btn_apply: 'மாற்றங்களைப் பயன்படுத்து',
    btn_switch: 'மாற்று',
    btn_logout: 'வெளியேறு',
    btn_profile: 'என் சுயவிவரம்',
    btn_change_password: 'கடவுச்சொல் மாற்று',
    btn_add_language: 'மொழி சேர்க்க',
    btn_save_default_lang: 'இயல்புநிலை மொழியைச் சேமி',

    // Statuses & Indicators
    status_active: 'செயலில் (Active)',
    status_inactive: 'செயலற்றது',
    status_pending: 'நிலுவையில்',
    status_completed: 'முடிந்தது',
    status_booked: 'முன்பதிவு செய்யப்பட்டது',
    status_available: 'கிடைக்கிறது',
    status_success: 'வெற்றி',
    status_error: 'பிழை',
    status_warning: 'எச்சரிக்கை',
    status_loading: 'ஏற்றுகிறது...',
    status_no_data: 'தரவு கிடைக்கவில்லை',
    priority_critical: 'மிக முக்கியம் (Critical)',
    priority_high: 'உயர் (High)',
    priority_medium: 'நடுத்தர (Medium)',
    priority_low: 'குறைந்த (Low)',

    // Table Headers
    th_hash: '#',
    th_language: 'மொழி',
    th_short_code: 'குறியீடு',
    th_actions: 'செயல்கள்',
    th_name: 'பெயர்',
    th_email: 'மின்னஞ்சல்',
    th_role: 'பொறுப்பு',
    th_organization: 'அமைப்பு',
    th_created_at: 'உருவாக்கப்பட்ட தேதி',
    th_status: 'நிலை',
    th_title: 'தலைப்பு',
    th_category: 'வகை',

    // Settings Tabs & Sections
    settings_title: 'அமைப்புகள் மற்றும் கட்டமைப்புகள்',
    settings_subtitle: 'அமைப்பு முன்னிருப்புகள், மொழிகள், பொறுப்புகள் & அனுமதிகளை நிர்வகிக்கவும்.',
    tab_languages: 'மொழிகள் (Languages)',
    tab_roles_permissions: 'பொறுப்புகள் & அனுமதிகள்',
    tab_approval_settings: 'ஒப்புதல் அமைப்புகள்',
    tab_feature_settings: 'அம்ச அமைப்புகள்',
    tab_app_home_settings: 'முகப்புப் பக்க அமைப்புகள்',
    tab_app_bottom_bar: 'கீழ் பட்டி அமைப்புகள்',
    tab_sunday_worship: 'ஞாயிறு ஆராதனை & வேதாகம படிப்பு',
    tab_faith_statement: 'விசுவாச அறிக்கை அமைப்புகள்',
    tab_my_daily_devotion: 'என் தினசரி தியானம்',
    org_default_language: 'அமைப்பின் முன்னிருப்பு மொழி',
    org_default_lang_desc: 'பயனர்களுக்கு தானாகவே ஏற்றப்படும் முதன்மை மொழியைத் தேர்ந்தெடுக்கவும்.',
    current_active_default: 'தற்போதைய இயல்புநிலை மொழி',
    system_supported_languages: 'ஆதரிக்கப்படும் மொழிகள்',
    search_language_placeholder: 'மொழி அல்லது குறியீட்டைத் தேடு...',
    select_default_lang_placeholder: 'முன்னிருப்பு மொழியைத் தேர்ந்தெடு...',

    // Theme & User Actions
    theme_dark_mode: 'இருண்ட பயன்முறைக்கு மாற்று',
    theme_light_mode: 'ஒளி பயன்முறைக்கு மாற்று',
  },

  // ── SPANISH (Español) ───────────────────────────────────────────────────────
  es: {
    brand_title: 'Red Global de Oración',
    brand_tagline: 'Uniendo el Cuerpo Global de Cristo • Oración, Devoción y Generosidad',
    admin_badge: 'Admin',
    super_admin_badge: 'Super Admin',
    compliance_tracker: 'Cumplimiento de Acuerdo',
    agreement_pending: 'Acuerdo Pendiente',
    select_language: 'Seleccionar Idioma',
    exit_org_view: 'Salir',
    switch_org: 'Cambiar Organización',
    visit_website: 'Visitar Sitio Web',

    nav_dashboard: 'Panel Principal',
    nav_users: 'Usuarios',
    nav_prayers: 'Oraciones',
    nav_prayer_closet: 'Aposento de Oración',
    nav_share_and_accept: 'Compartir y Recibir',
    nav_ride_connect: 'Conexión de Viajes',
    nav_songs: 'Canciones',
    nav_events: 'Eventos',
    nav_daily_devotions: 'Devocional Diario',
    nav_testimonies: 'Testimonios',
    nav_feedback: 'Comentarios',
    nav_groups: 'Grupos',
    nav_organizations: 'Organizaciones',
    nav_settings: 'Configuración',
    nav_admin_agreement: 'Acuerdo de Administrador',
    nav_guide: 'Guía y Ayuda',

    btn_save: 'Guardar',
    btn_cancel: 'Cancelar',
    btn_edit: 'Editar',
    btn_delete: 'Eliminar',
    btn_create: 'Crear',
    btn_add: 'Agregar Nuevo',
    btn_search: 'Buscar',
    btn_filter: 'Filtrar',
    btn_export: 'Exportar',
    btn_import: 'Importar',
    btn_submit: 'Enviar',
    btn_back: 'Volver',
    btn_confirm: 'Confirmar',
    btn_refresh: 'Actualizar',
    btn_close: 'Cerrar',
    btn_view: 'Ver',
    btn_actions: 'Acciones',
    btn_reset: 'Restablecer',
    btn_apply: 'Aplicar Cambios',
    btn_switch: 'Cambiar',
    btn_logout: 'Cerrar Sesión',
    btn_profile: 'Mi Perfil',
    btn_change_password: 'Cambiar Contraseña',
    btn_add_language: 'Agregar Idioma',
    btn_save_default_lang: 'Guardar Idioma Predeterminado',

    status_active: 'Activo',
    status_inactive: 'Inactivo',
    status_pending: 'Pendiente',
    status_completed: 'Completado',
    status_booked: 'Reservado',
    status_available: 'Disponible',
    status_success: 'Éxito',
    status_error: 'Error',
    status_warning: 'Advertencia',
    status_loading: 'Cargando...',
    status_no_data: 'No se encontraron datos',
    priority_critical: 'Crítico',
    priority_high: 'Alto',
    priority_medium: 'Medio',
    priority_low: 'Bajo',

    th_hash: '#',
    th_language: 'Idioma',
    th_short_code: 'Código',
    th_actions: 'Acciones',
    th_name: 'Nombre',
    th_email: 'Correo',
    th_role: 'Rol',
    th_organization: 'Organización',
    th_created_at: 'Creado El',
    th_status: 'Estado',
    th_title: 'Título',
    th_category: 'Categoría',

    settings_title: 'Ajustes y Configuraciones',
    settings_subtitle: 'Configure valores predeterminados, idiomas, roles y permisos de la organización.',
    tab_languages: 'Idiomas',
    tab_roles_permissions: 'Roles y Permisos',
    tab_approval_settings: 'Ajustes de Aprobación',
    tab_feature_settings: 'Ajustes de Funciones',
    tab_app_home_settings: 'Inicio de la App',
    tab_app_bottom_bar: 'Barra Inferior de la App',
    tab_sunday_worship: 'Culto Dominical y Estudio Bíblico',
    tab_faith_statement: 'Declaración de Fe',
    tab_my_daily_devotion: 'Mi Devocional Diario',
    org_default_language: 'Idioma Predeterminado de la Organización',
    org_default_lang_desc: 'Elija el idioma principal cargado automáticamente para los usuarios de la aplicación móvil.',
    current_active_default: 'Predeterminado Activo Actual',
    system_supported_languages: 'Idiomas Compatibles del Sistema',
    search_language_placeholder: 'Buscar idioma o código...',
    select_default_lang_placeholder: 'Seleccionar Idioma Predeterminado...',

    theme_dark_mode: 'Cambiar a Modo Oscuro',
    theme_light_mode: 'Cambiar a Modo Claro',
  },
};

/**
 * Intelligent helper to resolve static translations with fallback
 * @param {string} lang Target language code ('en', 'te', 'hi', etc.)
 * @param {string} key Dictionary key or English text
 * @param {string} fallback Optional fallback text
 */
export const getStaticText = (lang = 'en', key = '', fallback = '') => {
  if (!key) return fallback || '';

  const targetLang = lang?.toLowerCase() || 'en';
  const targetDict = staticTranslations[targetLang] || staticTranslations['en'];
  const englishDict = staticTranslations['en'];

  // 1. Direct key match in target dictionary
  if (targetDict && targetDict[key]) {
    return targetDict[key];
  }

  // 2. Direct key match in English dictionary
  if (englishDict && englishDict[key]) {
    // If target has it, return target
    if (targetDict && targetDict[key]) return targetDict[key];
    return englishDict[key];
  }

  // 3. Reverse lookup: if 'key' is actually an English value (e.g. key="Dashboard")
  if (englishDict) {
    const matchedKey = Object.keys(englishDict).find(
      (k) => englishDict[k]?.toLowerCase() === key.toLowerCase()
    );
    if (matchedKey && targetDict && targetDict[matchedKey]) {
      return targetDict[matchedKey];
    }
  }

  // 4. Return fallback or original key
  return fallback || key;
};

export default staticTranslations;
